import React from 'react';
import { Container } from 'components/Containers';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';

import useOnlineOrderingLocationInformation from 'hooks/useOnlineOrderingLocationInformation';
import { useOnlineOrderingDisplayLocationInformationOnLayout } from 'hooks/useDisplayLocationInformationOnLayout';
import selectHeroImage from 'bento-ordering/base/selectors/selectHeroImage';
import Avatar, { CIRCLE } from 'components/Avatar';
import {
  getPageBackgroundColor,
  getHeaderAccentLightestColor
} from 'styles/helpers/getThemeStyles';
import { getHasHeroImage } from 'helpers/getHasHeroImage';
import BaseScreenHero from 'bento-ordering/base/components/BaseScreenHero';
import useAvatarPath from 'bento-ordering/base/hooks/useAvatarPath';
import LayoutLocationDetails from 'bento-ordering/menus/components/LayoutLocationDetails';
import { selectBrandLogo } from 'bento-ordering/brand/selectors';

const CurvedCover = styled('div')(({ theme }) => {
  return {
    height: '100px',
    background: getPageBackgroundColor(theme),
    position: 'absolute',
    clipPath: 'ellipse(55% 85% at 50% 100%)',
    bottom: 0,
    left: 0,
    right: 0
  };
});

const StyledDetailsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative'
});

const StyledLogoContainer = styled(Container)({
  '&.MuiContainer-root': {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    right: 0,
    bottom: 0
  }
});

const StyledNoHeroHeaderWrapper = styled('div')(({ theme }) => ({
  backgroundColor: getHeaderAccentLightestColor(theme),
  height: '250px',
  width: '100%',
  zIndex: -2,
  position: 'relative'
}));

function CurvedScreenLogoWithHero({ heroImage, hasHeroImage, children }) {
  const heroOwnState = theme => ({
    [theme.breakpoints.down('sm')]: {
      height: 215
    }
  });

  return (
    <BaseScreenHero
      HeroContainerProps={{
        layout: 'curved',
        hasHeroImage
      }}
      HeroProps={{
        image: heroImage,
        heroOwnState: { hero: heroOwnState }
      }}
      heroContent={<CurvedCover data-testid="curved-cover" />}
    >
      {children}
    </BaseScreenHero>
  );
}

CurvedScreenLogoWithHero.propTypes = {
  heroImage: PropTypes.object,
  hasHeroImage: PropTypes.bool,
  children: PropTypes.object
};

function CurvedScreenLogoNoHero({ children }) {
  return (
    <Box position={'relative'}>
      <StyledNoHeroHeaderWrapper>
        <CurvedCover data-testid="curved-cover" />
      </StyledNoHeroHeaderWrapper>
      {children}
    </Box>
  );
}

CurvedScreenLogoNoHero.propTypes = {
  children: PropTypes.bool
};

function CurvedScreenLogoHero() {
  const logo = useSelector(selectBrandLogo);
  const heroImage = useSelector(selectHeroImage);
  const avatarPath = useAvatarPath();
  const shouldDisplayLocationInformationOnLayout = useOnlineOrderingDisplayLocationInformationOnLayout();
  const locationInformation = useOnlineOrderingLocationInformation();

  const hasHeroImage = getHasHeroImage(heroImage);
  const shouldRenderLocationInfo =
    shouldDisplayLocationInformationOnLayout && !!locationInformation;
  const hasLogo = !!logo;

  const HeroComponent = hasHeroImage
    ? CurvedScreenLogoWithHero
    : CurvedScreenLogoNoHero;

  const heroComponentProps = hasHeroImage
    ? {
        heroImage,
        hasHeroImage
      }
    : {};

  return (
    <HeroComponent {...heroComponentProps}>
      <Box position={'relative'}>
        {hasLogo && (
          <StyledLogoContainer>
            <Avatar src={logo} path={avatarPath} shape={CIRCLE} />
          </StyledLogoContainer>
        )}
      </Box>
      {shouldRenderLocationInfo && (
        <StyledDetailsContainer>
          <LayoutLocationDetails {...locationInformation} />
        </StyledDetailsContainer>
      )}
    </HeroComponent>
  );
}

export default CurvedScreenLogoHero;
